import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import useDownloadInvoices, { TakerMarketIds } from '@/enterprise/data/useDownloadInvoices';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { ButtonLink } from '@/shared/components/ButtonLink';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import DownloadInvoicesModal from './DownloadInvoicesModal';

const DownloadInvoices = () => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { mutate: downloadInvoices } = useDownloadInvoices();
  const [showDownloadInvoicesModal, setShowDownloadInvoicesModal] = useState(false);
  const { data: takerMarketsWithEligibleInvoices = [] } = useTakerMarkets(
    useCallback((takerMarkets: TakerMarket[]) => {
      const withEligibleInvoices: TakerMarketIds = [];

      takerMarkets.forEach((tm) => {
        if (tm.eligibleInvoiceCount > 0) {
          withEligibleInvoices.push({
            takerId: tm.takerDivisionId,
            marketUuid: tm.marketUuid,
          });
        }
      });

      return withEligibleInvoices;
    }, [])
  );

  const handleDownloadInvoices = () => {
    downloadInvoices({ takerMarketIds: takerMarketsWithEligibleInvoices });
    setShowDownloadInvoicesModal(true);
    trackEnterpriseEvent('download-invoices::submitted', { takerMarketIds: takerMarketsWithEligibleInvoices });
  };

  return takerMarketsWithEligibleInvoices.length > 0 ? (
    <>
      <DownloadInvoicesModal open={showDownloadInvoicesModal} onClose={() => setShowDownloadInvoicesModal(false)} />
      <IntercomDataTarget className="w-full md:w-fit" target="downloadAllInvoicesButton">
        <Button className="w-full md:w-fit" onClick={handleDownloadInvoices} variant="tertiary">
          {t('core.downloadInvoices')}
        </Button>
      </IntercomDataTarget>
    </>
  ) : null;
};

const BuildOfferButton = () => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const readOnlyUser = useReadOnlyUser();

  return (
    <IntercomDataTarget className="w-full md:w-fit" target="buildOfferButton">
      <ButtonLink
        className="w-full md:w-fit"
        disabled={readOnlyUser}
        onClick={() =>
          trackEnterpriseEvent('offer-builder::cta::clicked', {
            referrer: 'homepage',
          })
        }
        to={asEnterpriseLink('build-offer')}
        variant="cta"
      >
        {t('core.buildOffer')}
      </ButtonLink>
    </IntercomDataTarget>
  );
};

const HomepageHeader = ({ title }: { title: string }) => (
  <div className="mb-10 items-center justify-between space-y-4 md:flex md:space-y-0">
    <div>
      <h1 className="m-0 text-4xl font-medium">{title}</h1>
    </div>
    <div className="flex flex-col-reverse items-center gap-4 md:flex-row">
      <QueryBoundaries>
        <DownloadInvoices />
      </QueryBoundaries>
      <QueryBoundaries>
        <BuildOfferButton />
      </QueryBoundaries>
    </div>
  </div>
);

export default HomepageHeader;
